/**
 * Custom element for scroll-to-top functionality on product collection pages
 * Shows/hides based on product-grid-main-container visibility
 * Includes product counter showing visible vs total products
 * Also updates product-count elements to display the same counter text
 */
class ScrollToProductGrid extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.seenProducts = 0;
    this.totalProducts = parseInt(this.dataset.totalProducts) || 0;
    this.highestSeenIndex = -1; // Track the highest product index we've seen
    this.productsPerPage = this.getProductsPerPageFromDOM(); // Track products per page for URL calculation
    this.currentURLPage = this.getCurrentPageFromURL(); // Track current URL page to avoid unnecessary updates
    this.initialPage = this.getCurrentPageFromURL(); // Track the page we started on
    this.initialProductCount = 0; // Track how many products were initially loaded
    this.totalPages = this.getTotalPagesFromDOM(); // Track total available pages
    this.loadedPages = new Set([this.initialPage]); // Track which pages have been loaded
    this.loadMoreURLUpdateTimeout = null; // Track recent load-more URL updates
    this.isLoadMoreUpdating = false; // Flag to prevent conflicts with load-more
    this.init();
  }

  init() {
    this.createElements();
    this.setupScrollListener();
    this.setupClickHandler();
    this.setupProductObserver();
    this.setupFilterListener();
    this.setupLoadMoreCoordination();
    
    // Clean up URL if we're on page=1 (should have no page parameter)
    this.cleanupInitialURL();
    
    // Initial check for product count visibility after everything is set up
    setTimeout(() => {
      // Store original text for all product count elements
      this.storeOriginalTexts();
      
      // Count initial products to establish baseline
      this.initializeProductBaseline();
      
      if (this.isVisible) {
        this.showProductCounts();
      } else {
        // Ensure original text is shown when at top
        this.hideProductCounts();
      }
    }, 200);
  }

  /**
   * Clean up the initial URL if it has page=1 parameter
   * page=1 is equivalent to no page parameter and should be redirected
   */
  cleanupInitialURL() {
    const currentUrl = new URL(window.location);
    const pageParam = currentUrl.searchParams.get('page');
    
    // If page parameter is 1, redirect to clean URL since it's redundant
    if (pageParam === '1') {
      currentUrl.searchParams.delete('page');
      
      // Also clean phcursor parameter for consistency
      currentUrl.searchParams.delete('phcursor');
      
      const cleanUrl = currentUrl.pathname + (currentUrl.search || '');
      
      // Perform a redirect (replaces current page in history, equivalent to 301)
      // This is better for SEO than just updating the URL
      window.location.replace(cleanUrl);
    }
  }

  /**
   * Create the scroll button and counter elements
   */
  createElements() {
    // Create scroll button
    this.scrollButton = document.createElement('button');
    this.scrollButton.className = 'scroll-button';
    this.scrollButton.setAttribute('aria-label', theme.strings.scrollToTop);
    this.scrollButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14L12 9L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Create counter display
    this.counterDisplay = document.createElement('div');
    this.counterDisplay.className = 'product-counter';
    this.counterDisplay.textContent = theme.strings.scrollToTop;

    // Add elements to the custom element (button first, then counter)
    this.appendChild(this.scrollButton);
    this.appendChild(this.counterDisplay);
  }

  /**
   * Setup scroll listener to watch product grid container
   */
  setupScrollListener() {
    const productGridContainer = document.querySelector('.product-grid-main-container');
    
    if (!productGridContainer) {
      console.warn('ScrollToProductGrid: product-grid-main-container not found');
      return;
    }

    let scrollTimeout;
    
    const checkVisibility = () => {
      const rect = productGridContainer.getBoundingClientRect();
      const isTopVisible = rect.top >= 0;
      
      if (!isTopVisible && !this.isVisible) {
        // Top of product grid is not visible (scrolled past) - show button and product counts
        this.isVisible = true;
        this.classList.add('visible');
        this.showProductCounts();
      } else if (isTopVisible && this.isVisible) {
        // Top of product grid is visible - hide button and product counts
        this.isVisible = false;
        this.classList.remove('visible');
        this.hideProductCounts();
      }
    };

    // Check initial state after a brief delay to ensure DOM is ready
    setTimeout(() => {
      checkVisibility();
    }, 100);

    // Listen for scroll events with throttling
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkVisibility, 50);
    }, { passive: true });

    // Store the cleanup function
    this.scrollCleanup = () => {
      clearTimeout(scrollTimeout);
    };
  }

  /**
   * Setup click handler for smooth scroll to product grid
   */
  setupClickHandler() {
    this.scrollButton.addEventListener('click', () => {
      const productGridContainer = document.querySelector('.product-grid-main-container');
      
      if (productGridContainer) {
        const targetRect = productGridContainer.getBoundingClientRect();
        const targetTop = targetRect.top + window.pageYOffset - 20; // 20px offset above the element
        
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth'
        });
      } else {
        // Fallback to scroll to top
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    });
  }

  /**
   * Setup intersection observer to track seen products
   */
  setupProductObserver() {
    // Initial count of products
    this.updateProductCount();

    // Observer for when products come into view (tracking what we've seen)
    const observerOptions = {
      root: null,
      rootMargin: '0px', // No margin - trigger exactly when entering/leaving viewport
      threshold: [0, 0.1] // Trigger on both entry and exit
    };

    this.productObserver = new IntersectionObserver((entries) => {
      // Throttle the updates to avoid too many calculations
      if (!this.updateTimeout) {
        this.updateTimeout = setTimeout(() => {
          this.updateSeenProducts(entries);
          this.updateTimeout = null;
        }, 100);
      }
    }, observerOptions);

    // Start observing existing products
    this.observeProducts();

    // Watch for new products being added (for load more functionality)
    this.setupProductAdditionWatcher();

    // Add scroll-based fallback for smooth counter updates
    this.setupScrollBasedUpdates();
  }

  /**
   * Setup scroll-based updates as a fallback for smooth counter updates
   * Also handles reverse URL updating when scrolling up
   */
  setupScrollBasedUpdates() {
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateHighestVisibleProduct();
        this.updateCounterDisplay();
        this.updateURLBasedOnVisibleProducts();
      }, 50);
    }, { passive: true });
  }

  /**
   * Store original text and create dual span structure
   */
  storeOriginalTexts() {
    const productCountElements = document.querySelectorAll('.product-count');
    productCountElements.forEach(element => {
      const textSpan = element.querySelector('#ProductCount, #ProductCountDesktop');
      if (textSpan && !textSpan.dataset.dualSpansCreated) {
        const originalText = textSpan.textContent.trim();
        
        // Create original text span
        const originalSpan = document.createElement('span');
        originalSpan.className = 'product-count__original';
        originalSpan.textContent = originalText;
        
        // Create counter text span
        const counterSpan = document.createElement('span');
        counterSpan.className = 'product-count__counter';
        counterSpan.textContent = originalText; // Default to original text
        
        // Clear the original content and add both spans
        textSpan.textContent = '';
        textSpan.appendChild(originalSpan);
        textSpan.appendChild(counterSpan);
        
        // Mark as processed
        textSpan.dataset.dualSpansCreated = 'true';
        textSpan.dataset.originalText = originalText;
      }
    });
  }

  /**
   * Show product count elements and swap to counter text
   */
  showProductCounts() {
    const productCountElements = document.querySelectorAll('.product-count');
    productCountElements.forEach(element => {
      element.classList.add('show-with-scroll');
      this.updateCounterSpan(element);
      element.classList.add('show-counter');
    });
  }

  /**
   * Hide product count elements and swap back to original text
   */
  hideProductCounts() {
    const productCountElements = document.querySelectorAll('.product-count');
    productCountElements.forEach(element => {
      element.classList.remove('show-with-scroll');
      element.classList.remove('show-counter');
    });
  }

  /**
   * Update the counter span with current counter text
   */
  updateCounterSpan(element) {
    const textSpan = element.querySelector('#ProductCount, #ProductCountDesktop');
    if (!textSpan) return;

    const counterSpan = textSpan.querySelector('.product-count__counter');
    if (counterSpan) {
      // Ensure we have a valid seen products count
      if (this.seenProducts === 0) {
        this.updateHighestVisibleProduct();
      }

      // Generate counter text
      const counterText = theme.strings.productsSeen
        .replace('[productsViewed]', this.seenProducts)
        .replace('[paginateItems]', this.totalProducts);

      counterSpan.textContent = counterText;
    }
  }


  /**
   * Update the counter display (total products is already set from data attribute)
   */
  updateProductCount() {
    // Total products is set from the data attribute and doesn't change
    // This method now just updates the display
    this.updateCounterDisplay();
  }

  /**
   * Observe all product cards for seen tracking
   */
  observeProducts() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const products = productGrid.querySelectorAll('.grid__item:not(.card-description):not(.grid__item--loader)');
    
    // Stop observing previous products
    if (this.productObserver) {
      this.productObserver.disconnect();
    }

    // Start observing all products and assign index data
    products.forEach((product, index) => {
      product.dataset.productIndex = index;
      this.productObserver.observe(product);
    });
  }

  /**
   * Update seen product count based on intersection observer entries
   */
  updateSeenProducts(entries) {
    // Simple approach: find the highest index product that's currently visible
    this.updateHighestVisibleProduct();
    this.updateCounterDisplay();
  }

  /**
   * Find the highest index product currently visible and update the counter
   */
  updateHighestVisibleProduct() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const products = productGrid.querySelectorAll('.grid__item:not(.card-description):not(.grid__item--loader)');
    let highestVisibleIndex = -1;

    // Check each product to see if it's visible
    products.forEach((product, index) => {
      const rect = product.getBoundingClientRect();
      // Consider a product "seen" if any part of it has been visible
      // We use a more generous threshold - if the top is above the bottom of the viewport
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible && index > highestVisibleIndex) {
        highestVisibleIndex = index;
      }
    });

    // Update seen products count based on the highest visible product
    // If no products are visible, keep the previous count
    if (highestVisibleIndex >= 0) {
      this.seenProducts = highestVisibleIndex + 1; // +1 because index is 0-based
      this.highestSeenIndex = highestVisibleIndex;
    }
  }

  /**
   * Watch for new products being added to the grid (load more functionality)
   */
  setupProductAdditionWatcher() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    // Use MutationObserver to watch for new product additions
    this.mutationObserver = new MutationObserver((mutations) => {
      let newProductsAdded = false;

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && 
              node.classList.contains('grid__item') && 
              !node.classList.contains('card-description') &&
              !node.classList.contains('grid__item--loader')) {
            newProductsAdded = true;
          }
        });
      });

      if (newProductsAdded) {
        // Re-observe products when new ones are added, but don't change total count
        this.observeProducts();
      }
    });

    this.mutationObserver.observe(productGrid, {
      childList: true,
      subtree: false
    });
  }

  /**
   * Setup listener for filter changes to update product count
   */
  setupFilterListener() {
    // Watch for changes to the product grid container (happens when filters are applied)
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    // Use MutationObserver to watch for filter-induced changes
    this.filterObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'subtree') {
          // Check if the product grid content has been replaced (filter update)
          this.handleFilterUpdate();
        }
      });
    });

    this.filterObserver.observe(productGridContainer, {
      childList: true,
      subtree: true
    });

    // Also listen for the specific collection loading class changes
    const collectionElement = productGridContainer.querySelector('.collection');
    if (collectionElement) {
      const classObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            // When loading class is removed, filter update is complete
            if (!target.classList.contains('loading')) {
              setTimeout(() => this.handleFilterUpdate(), 100); // Small delay to ensure DOM is fully updated
            }
          }
        });
      });

      classObserver.observe(collectionElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  /**
   * Handle filter updates by extracting the new total count and resetting seen count
   */
  handleFilterUpdate() {
    // Check if this is triggered by DOM changes from loading pages
    // If so, we should ignore it to avoid resetting state
    const recentLoadPrev = performance.now() - (this.lastLoadPrevTime || 0) < 5000; // 5 seconds
    const recentLoadMore = performance.now() - (this.lastLoadMoreTime || 0) < 5000; // 5 seconds
    
    if (recentLoadPrev || recentLoadMore) {
      return;
    }
    // Check if this is actually a filter change by examining URL parameters
    const currentUrl = window.location.search;
    const urlParams = new URLSearchParams(currentUrl);
    
    // Count actual filter parameters (excluding availability filter and page/sort params)
    let filterCount = 0;
    for (const [key] of urlParams) {
      if (key.startsWith('filter.') && key !== 'filter.v.availability') {
        filterCount++;
      }
    }
    
    // Has filters if there are filter params OR if no URL params at all (clearing filters)
    const hasFilters = filterCount > 0;
    const isFilterClear = filterCount === 0 && this.totalProducts !== parseInt(this.dataset.totalProducts);
    
    // Update total if filters are applied OR if filters were cleared
    if (hasFilters || isFilterClear) {
      const newTotal = this.extractFilteredProductCount();
      if (newTotal !== null) {
        this.totalProducts = newTotal;
      }
      // If clearing filters, revert to original total
      if (isFilterClear && newTotal === null) {
        this.totalProducts = parseInt(this.dataset.totalProducts) || 0;
      }
    }
    
    // Always reset seen products tracking when content changes
    this.resetSeenProducts();
    // Re-observe products with new grid
    this.observeProducts();
    // Recalculate products per page (might change with filters)
    this.productsPerPage = this.getProductsPerPageFromDOM();
    // Reset URL page tracking and baseline context
    this.currentURLPage = this.getCurrentPageFromURL();
    this.initialPage = this.currentURLPage;
    this.loadedPages = new Set([this.currentURLPage]); // Reset loaded pages tracking
    this.totalPages = this.getTotalPagesFromDOM();
    this.initializeProductBaseline();
    // Store new original texts after filter update
    setTimeout(() => {
      this.storeOriginalTexts();
      // Restore proper text state based on scroll position
      if (this.isVisible) {
        this.showProductCounts();
      } else {
        this.hideProductCounts();
      }
      
      // Notify load-prev-pages component about filter change
      const loadPrevElement = document.querySelector('load-prev-pages');
      if (loadPrevElement && loadPrevElement.loadedPages) {
        // Only reset if this is actually a filter change, not just a regular update
        // For now, let's not reset the loaded pages as it interferes with the load previous functionality
        loadPrevElement.updateVisibility();
      }
    }, 100);
    this.updateCounterDisplay();
  }

  /**
   * Extract the filtered product count from the facets product count display
   */
  extractFilteredProductCount() {
    const productCountElement = document.getElementById('ProductCount');
    if (!productCountElement) return null;

    const countText = productCountElement.textContent || productCountElement.innerText;
    
    // Try to extract the total count from various product count formats
    // Examples: "24 of 99 products" -> 99, "24 products" -> 24, "99 results" -> 99
    const patterns = [
      /\d+\s+of\s+(\d+)\s+products?/i,             // "24 of 99 products" -> extract 99 (total)
      /showing\s+\d+\s+of\s+(\d+)/i,               // "Showing 24 of 99" -> extract 99 (total)
      /(\d+)\s+(?:products?|results?)/i,           // "24 products" or "99 results" -> extract the single number
      /^(\d+)/                                      // Just a number at the start
    ];

    for (const pattern of patterns) {
      const match = countText.match(pattern);
      if (match) {
        return parseInt(match[1]);
      }
    }

    // Fallback: count actual products in DOM
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      const products = productGrid.querySelectorAll('.grid__item:not(.card-description):not(.grid__item--loader)');
      return products.length;
    }

    return null;
  }

  /**
   * Reset seen products tracking
   */
  resetSeenProducts() {
    this.seenProducts = 0;
    this.highestSeenIndex = -1;
    
    // Clear any pending timeouts
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

  /**
   * Update the counter display text
   */
  updateCounterDisplay() {
    const counterText = theme.strings.scrollToTop;
    
    // Update scroll button counter
    if (this.counterDisplay) {
      this.counterDisplay.textContent = counterText;
    }
    
    // Update product-count elements with counter text only if scroll component is visible
    if (this.isVisible) {
      const productCountElements = document.querySelectorAll('.product-count');
      productCountElements.forEach(element => {
        this.updateCounterSpan(element);
      });
    }
  }

  /**
   * Get the products per page value from DOM elements
   * @returns {number} Number of products per page
   */
  getProductsPerPageFromDOM() {
    // Try to get from load-prev-pages data first (most reliable source)
    const loadPrevElement = document.querySelector('load-prev-pages[data-products-per-page]');
    if (loadPrevElement && loadPrevElement.dataset.productsPerPage) {
      return parseInt(loadPrevElement.dataset.productsPerPage, 10);
    }
    
    // Try to get from load-more-button data
    const loadMoreButton = document.querySelector('load-more-button [data-page-size]');
    if (loadMoreButton && loadMoreButton.value) {
      return parseInt(loadMoreButton.value, 10);
    }
    
    // Don't count DOM products as this gives wrong results when multiple pages are loaded
    // Just use the standard page size
    return 24;
  }

  /**
   * Get current page number from URL
   * @returns {number} Current page number (defaults to 1)
   */
  getCurrentPageFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  }

  /**
   * Get total pages from DOM elements (from load-more button data)
   * @returns {number} Total number of pages available
   */
  getTotalPagesFromDOM() {
    const loadMoreButton = document.querySelector('load-more-button [data-total-pages]');
    if (loadMoreButton && loadMoreButton.value) {
      return parseInt(loadMoreButton.value, 10);
    }
    
    // Fallback: calculate from total products and products per page
    if (this.totalProducts > 0 && this.productsPerPage > 0) {
      return Math.ceil(this.totalProducts / this.productsPerPage);
    }
    
    return 1;
  }

  /**
   * Initialize the baseline product count to establish the starting context
   */
  initializeProductBaseline() {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      const products = productGrid.querySelectorAll('.grid__item:not(.card-description):not(.grid__item--loader)');
      this.initialProductCount = products.length;
    }
  }

  /**
   * Calculate what page the user should be on based on visible products
   * @returns {number} The calculated page number relative to loaded pages
   */
  calculateCurrentPageFromVisibleProducts() {
    if (this.seenProducts <= 0 || this.productsPerPage <= 0) {
      return this.currentURLPage || this.initialPage;
    }
    
    // Get the range of loaded pages
    const loadedPagesArray = Array.from(this.loadedPages).sort((a, b) => a - b);
    if (loadedPagesArray.length === 0) {
      return this.currentURLPage || this.initialPage;
    }
    
    const earliestPage = loadedPagesArray[0];
    const latestPage = loadedPagesArray[loadedPagesArray.length - 1];
    
    // Calculate which page based on visible products, starting from the earliest loaded page
    // We need to be more careful with the calculation since the last page might have fewer products
    let calculatedPage = earliestPage;
    let accumulatedProducts = 0;
    
    // Go through each loaded page and see which one contains our seenProducts
    for (const pageNum of loadedPagesArray) {
      const isLastPage = pageNum === this.totalPages;
      const productsInThisPage = isLastPage ? 
        Math.min(this.productsPerPage, this.totalProducts - ((pageNum - 1) * this.productsPerPage)) : 
        this.productsPerPage;
      
      if (this.seenProducts <= accumulatedProducts + productsInThisPage) {
        calculatedPage = pageNum;
        break;
      }
      accumulatedProducts += productsInThisPage;
    }
    
    // Ensure we stay within the range of actually loaded pages
    const clampedPage = Math.max(earliestPage, Math.min(calculatedPage, latestPage));
    
    
    return clampedPage;
  }

  /**
   * Get the total number of products currently loaded in the DOM
   * @returns {number} Total loaded product count
   */
  getTotalLoadedProducts() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return this.initialProductCount;
    
    const products = productGrid.querySelectorAll('.grid__item:not(.card-description):not(.grid__item--loader)');
    return products.length;
  }

  /**
   * Update URL based on currently visible products
   * Updates page parameter when scrolling up/down through different page boundaries
   * Respects load-more URL updates by giving them priority
   */
  updateURLBasedOnVisibleProducts() {
    // Don't update URL if load-more recently updated it (give load-more priority)
    if (this.isLoadMoreUpdating) {
      return;
    }
    
    const calculatedPage = this.calculateCurrentPageFromVisibleProducts();
    
    // Only update URL if the calculated page is different from current URL page
    if (calculatedPage !== this.currentURLPage) {
      this.updateURLToPage(calculatedPage);
      this.currentURLPage = calculatedPage;
    }
  }

  /**
   * Update the URL to reflect a specific page number
   * @param {number} pageNumber - The page number to set in URL
   */
  updateURLToPage(pageNumber) {
    const currentUrl = new URL(window.location);
    
    if (pageNumber === 1) {
      // For page 1, remove the page parameter entirely
      currentUrl.searchParams.delete('page');
    } else {
      // For other pages, set the page parameter
      currentUrl.searchParams.set('page', pageNumber.toString());
    }
    
    // Clean phcursor parameter for consistency
    currentUrl.searchParams.delete('phcursor');
    
    const newUrl = currentUrl.pathname + (currentUrl.search || '');
    
    // Update browser URL without triggering page reload
    window.history.replaceState({}, '', newUrl);
    
    // Update canonical URL
    this.updateCanonicalURL(newUrl);
  }

  /**
   * Update canonical URL in document head
   * @param {string} url - The new canonical URL path
   */
  updateCanonicalURL(url) {
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      // Create full URL from relative path
      const fullUrl = new URL(url, window.location.origin).href;
      canonicalLink.href = fullUrl;
    }
  }


  /**
   * Build a URL for a specific page number
   * @param {number} pageNumber - The page number
   * @returns {string} The URL for that page
   */
  buildPageURL(pageNumber) {
    const currentUrl = new URL(window.location);
    
    if (pageNumber === 1) {
      // For page 1, remove the page parameter entirely
      currentUrl.searchParams.delete('page');
    } else {
      // For other pages, set the page parameter
      currentUrl.searchParams.set('page', pageNumber.toString());
    }
    
    // Clean phcursor parameter for consistency
    currentUrl.searchParams.delete('phcursor');
    
    return currentUrl.pathname + (currentUrl.search || '');
  }

  /**
   * Setup coordination with load-more component to avoid URL update conflicts
   * Load-more takes priority, and this component syncs its tracking accordingly
   */
  setupLoadMoreCoordination() {
    // Listen for load-more and load-prev URL updates
    document.addEventListener('loadmore:url-updated', (event) => {
      if (event.detail.source === 'load-more' || event.detail.source === 'load-prev') {
        // Sync our page tracking with load-more's URL update
        this.currentURLPage = this.getCurrentPageFromURL();
        
        // Update our baseline context when load-more changes the URL
        // This ensures our relative calculations remain accurate
        this.initialPage = this.currentURLPage;
        this.loadedPages.add(this.currentURLPage); // Track that this page is now loaded
        this.initializeProductBaseline();
        
        // The load-prev-pages component will automatically sync via the 'loadmore:url-updated' event
        // No need for manual notification here
        
        // Mark that a load operation happened (to prevent filter update interference)
        if (event.detail.source === 'load-more') {
          this.lastLoadMoreTime = performance.now();
        } else if (event.detail.source === 'load-prev') {
          this.lastLoadPrevTime = performance.now();
        }
        
        // Temporarily disable our URL updates to give load-more priority
        this.isLoadMoreUpdating = true;
        
        // Clear any existing timeout
        if (this.loadMoreURLUpdateTimeout) {
          clearTimeout(this.loadMoreURLUpdateTimeout);
        }
        
        // Re-enable our URL updates after a delay (load-more should be done by then)
        this.loadMoreURLUpdateTimeout = setTimeout(() => {
          this.isLoadMoreUpdating = false;
          this.loadMoreURLUpdateTimeout = null;
        }, 2000); // 2 second delay to allow load-more to finish
      }
    });
  }

  /**
   * Cleanup method called when component is removed
   */
  disconnectedCallback() {
    // Clear any pending timeouts
    if (this.loadMoreURLUpdateTimeout) {
      clearTimeout(this.loadMoreURLUpdateTimeout);
      this.loadMoreURLUpdateTimeout = null;
    }
    
    // Clear scroll cleanup if it exists
    if (this.scrollCleanup) {
      this.scrollCleanup();
    }
    
    // Clear update timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

}

// LoadPrevPages is defined in sc-load-more.js
customElements.define('scroll-to-product-grid', ScrollToProductGrid);
